import { Drawer, Group, Loader, SegmentedControl, Switch, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useMarkChangelogSeen } from '../api/hooks';
import type { ChangelogEntry, ChangelogPayload } from '../api/types';
import { useUi } from '../state/ui';
import { actorLabel, useCoordinatorLabel } from '../lib/people';

const CATEGORIES = [
  { value: 'all', label: 'Vse' },
  { value: 'shift', label: 'Izmene' },
  { value: 'volunteer', label: 'Ljudje' },
  { value: 'external_group', label: 'Ekipe' },
  { value: 'post', label: 'Mesta' },
  { value: 'tag', label: 'Oznake' },
  { value: 'route', label: 'Trase' },
];

/** kind → Slovenian entry title. Shared with the Mesta editor's history card. */
export function entryTitle(e: ChangelogEntry): string {
  const map: Record<string, Record<string, string> | string> = {
    created: {
      shift: 'Nova izmena',
      post: 'Novo mesto',
      sector: 'Nov sektor',
      volunteer: 'Nov prostovoljec',
      external_group: 'Nova zunanja ekipa',
      tag: 'Oznaka dodana',
    },
    updated: {
      shift: 'Sprememba izmene',
      post: 'Urejeno mesto',
      post_window: 'Okno mesta',
      sector: 'Urejen sektor',
      volunteer: 'Urejen prostovoljec',
      external_group: 'Urejena zunanja ekipa',
      tag: 'Urejena oznaka',
      coordinator: 'Preimenovanje',
      route: 'Urejena trasa',
    },
    deleted: {
      shift: 'Izbris izmene',
      post: 'Izbris mesta',
      external_group: 'Izbris zunanje ekipe',
      tag: 'Izbris oznake',
      route: 'Izbris trase',
    },
    deactivated: { volunteer: 'Izbris prostovoljca' },
    cancelled: { volunteer: 'Ne pride' },
    uncancelled: { volunteer: 'Vendarle pride' },
    freed: { shift: 'Izmena sproščena' },
    restored: { shift: 'Izmena vrnjena', coordinator: 'Dostop vrnjen' },
    'photo-added': 'Fotografija dodana',
    'photo-updated': 'Opis fotografije',
    'photo-removed': 'Izbris fotografije',
    'photos-reordered': 'Vrstni red fotografij',
    'slots-set': 'Število oseb v ekipi',
    'confirmation-set': 'Potrditev',
    'shift-answer-set': 'Odgovor na izmeno',
    'tags-updated': 'Oznake urejene',
    imported: { volunteer: 'Uvoz prostovoljcev', route: 'Uvožena trasa' },
    'token-issued': 'Povezava do brifinga',
    'token-rotated': 'Nova povezava do brifinga',
    'briefing-published': 'Razpored objavljen brez pošte',
    'briefing-publish-skipped': 'Razpored ni bil objavljen',
    login: 'Prijava',
    'note-added': 'Opomba med ekipama',
    granted: 'Dostop dodeljen',
    'role-changed': 'Vloga spremenjena',
    renamed: 'Ime spremenjeno',
    'reply-class-set': 'Oznaka odgovora spremenjena',
    revoked: 'Dostop odvzet',
    'email-sent': 'E-pošta poslana',
    'briefing-sent': 'Napotnica poslana',
    'mail-uvodno': 'Uvodno pismo poslano',
    'mail-napotnica': 'Napotnica poslana',
    'mail-sprememba': 'Obvestilo o spremembi poslano',
    'mail-zahvala': 'Zahvala poslana',
    'mail-obvestilo': 'Obvestilo poslano',
    'mail-dnevno': 'Dnevno sporočilo poslano',
    'mail-template-updated': 'Predloga sporočila urejena',
    'mail-template-reset': 'Predloga povrnjena na privzeto',
    'mail-test-sent': 'Poskusno sporočilo',
    'day-mail-run': 'Dnevno sporočilo — pošiljanje',
    'day-mail-auto-on': 'Dnevno sporočilo: samodejno vklopljeno',
    'day-mail-auto-off': 'Dnevno sporočilo: samodejno izklopljeno',
    generated: {
      ai_summary: 'Povzetek pogovora osvežen',
      ai_draft: 'Osnutek odgovora ustvarjen',
      ai_digest: 'Dnevni povzetek osvežen',
    },
  };
  const byKind = map[e.kind];
  if (typeof byKind === 'string') return byKind;
  return byKind?.[e.entity] ?? `${e.entity} · ${e.kind}`;
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const dm = `${d.getDate()}. ${d.getMonth() + 1}.`;
  if (d.toDateString() === today.toDateString()) return `DANES · ${dm}`;
  if (d.toDateString() === yesterday.toDateString()) return `VČERAJ · ${dm}`;
  const weekday = d.toLocaleDateString('sl-SI', { weekday: 'long' }).toUpperCase();
  return `${weekday} · ${dm}`;
}

function rel(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 2) return 'pravkar';
  if (min < 60) return `${min} min`;
  if (min < 480) return `${Math.floor(min / 60)} h`;
  return '';
}

export function ChangelogDrawer() {
  const labelOf = useCoordinatorLabel();
  const { changelogOpen, setChangelogOpen } = useUi();
  const [category, setCategory] = useState('all');
  const [onlyBreaking, setOnlyBreaking] = useState(false);
  const [limit, setLimit] = useState(80);
  const markSeen = useMarkChangelogSeen();

  const query = useQuery({
    queryKey: ['changelog', 'drawer', category, limit],
    queryFn: () =>
      api<ChangelogPayload>(
        `/changelog?limit=${limit}${category !== 'all' ? `&entity=${category}` : ''}`,
      ),
    enabled: changelogOpen,
  });

  const markSeenMutate = markSeen.mutate;
  useEffect(() => {
    if (changelogOpen) markSeenMutate();
  }, [changelogOpen, markSeenMutate]);

  const entries = useMemo(() => {
    const list = query.data?.entries ?? [];
    return onlyBreaking ? list.filter((e) => e.breaking) : list;
  }, [query.data, onlyBreaking]);

  const grouped = useMemo(() => {
    const byDay = new Map<string, ChangelogEntry[]>();
    for (const e of entries) {
      const key = new Date(e.createdAt).toDateString();
      const list = byDay.get(key);
      if (list) list.push(e);
      else byDay.set(key, [e]);
    }
    return [...byDay.entries()];
  }, [entries]);

  return (
    <Drawer
      opened={changelogOpen}
      onClose={() => setChangelogOpen(false)}
      position="right"
      size={476}
      title={
        <div>
          <Text fw={700}>Dnevnik sprememb</Text>
          <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--rr-font-mono)' }}>
            {entries.length} vnosov
          </Text>
        </div>
      }
    >
      <Group gap={8} mb="sm" wrap="nowrap">
        <SegmentedControl
          size="xs"
          data={CATEGORIES}
          value={category}
          onChange={setCategory}
          fullWidth
          style={{ flex: 1 }}
        />
      </Group>
      <Switch
        size="xs"
        label="Samo prelomne"
        checked={onlyBreaking}
        onChange={(e) => setOnlyBreaking(e.currentTarget.checked)}
        mb="sm"
        color="red"
      />

      {query.isPending ? (
        <Loader size="sm" mx="auto" display="block" my="xl" />
      ) : entries.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          V izbranem obsegu ni sprememb.
        </Text>
      ) : (
        grouped.map(([key, list]) => (
          <div key={key}>
            <Text
              size="xs"
              fw={700}
              c="dimmed"
              mt="sm"
              mb={4}
              style={{ letterSpacing: '0.06em' }}
            >
              {dayLabel(list[0]!.createdAt)}
            </Text>
            {list.map((e) => (
              <Group
                key={e.id}
                gap={8}
                wrap="nowrap"
                py={5}
                style={{
                  borderBottom: '1px solid var(--rr-border)',
                  borderLeft: e.breaking ? '3px solid oklch(0.62 0.15 25)' : '3px solid transparent',
                  paddingLeft: 6,
                  background: e.breaking ? 'oklch(0.995 0.008 25)' : undefined,
                }}
              >
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ fontFamily: 'var(--rr-font-mono)', width: 36, flexShrink: 0 }}
                >
                  {new Date(e.createdAt).toLocaleTimeString('sl-SI', {
                    hour: '2-digit',
                    hourCycle: 'h23',
                    minute: '2-digit',
                  })}
                </Text>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" truncate>
                    <Text component="span" size="xs" fw={600}>
                      {actorLabel(e, labelOf)}
                    </Text>{' '}
                    ·{' '}
                    <Text component="span" size="xs" fw={600} c={e.breaking ? 'red.8' : undefined}>
                      {entryTitle(e)}
                    </Text>
                    {e.entityLabel ? (
                      <Text
                        component="span"
                        size="xs"
                        c="dimmed"
                        style={{ fontFamily: 'var(--rr-font-mono)' }}
                      >
                        {' '}
                        {e.entityLabel}
                      </Text>
                    ) : null}
                  </Text>
                </div>
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                  {rel(e.createdAt)}
                </Text>
              </Group>
            ))}
          </div>
        ))
      )}

      {query.data && query.data.entries.length >= limit ? (
        <Text
          size="xs"
          c="dimmed"
          ta="center"
          py="sm"
          style={{ cursor: 'pointer' }}
          onClick={() => setLimit((l) => l + 80)}
        >
          Naloži starejše
        </Text>
      ) : null}
    </Drawer>
  );
}
